import { Logger } from "@moment/logging";

import { env } from "../env";
import { writeTextFile } from "../filesystem/filesystem.internal";
import { getDocumentDotMomentPath } from "../paths/paths.internal";

const logger = new Logger("runtime-error-log");

const LOG_FILENAME = "runtime-errors.log";
const MAX_LOG_SIZE_BYTES = 512 * 1024; // 512 KB buffer limit
const TRUNCATE_TO_BYTES = 256 * 1024; // when truncating, keep last 256 KB

type ErrorSource = "code-cell" | "request-page";

interface RuntimeErrorEntry {
	timestamp: string;
	source: ErrorSource;
	documentId: string;
	pageId?: string;
	cellId?: string;
	pageName?: string;
	message: string;
	stack?: string;
}

function formatEntry(entry: RuntimeErrorEntry): string {
	const parts = [
		`[${entry.timestamp}] [${entry.source}]`,
		entry.documentId ? `doc=${entry.documentId}` : "",
		entry.pageId ? `page=${entry.pageId}` : "",
		entry.cellId ? `cell=${entry.cellId}` : "",
		entry.pageName ? `name=${entry.pageName}` : "",
	]
		.filter(Boolean)
		.join(" ");

	const lines = [`${parts}`, `  Error: ${entry.message}`];
	if (entry.stack) {
		const stackLines = entry.stack
			.split("\n")
			.slice(0, 10)
			.map((l) => `  ${l.trim()}`);
		lines.push(...stackLines);
	}
	lines.push("---");
	return lines.join("\n");
}

function getTimestamp(): string {
	return new Date().toISOString();
}

/**
 * Truncate log content if it exceeds the maximum size.
 * Keeps the most recent entries (tail of the file).
 */
function truncateIfNeeded(content: string): string {
	const bytes = new TextEncoder().encode(content);
	if (bytes.length <= MAX_LOG_SIZE_BYTES) {
		return content;
	}

	const truncated = new TextDecoder().decode(bytes.slice(bytes.length - TRUNCATE_TO_BYTES));
	const firstSeparator = truncated.indexOf("---\n");
	if (firstSeparator >= 0) {
		return (
			`[Log truncated at ${getTimestamp()} — older entries removed]\n---\n` +
			truncated.slice(firstSeparator + 4)
		);
	}
	return `[Log truncated at ${getTimestamp()}]\n${truncated}`;
}

// In-memory cache per document to batch writes and avoid reading the file every time
const logCache = new Map<string, string>();
const writeQueue = new Map<string, Promise<void>>();

async function getLogFilePath(documentId: string): Promise<string> {
	const { join } = await import("@tauri-apps/api/path");
	const dotMomentPath = await getDocumentDotMomentPath(documentId);
	return join(dotMomentPath, LOG_FILENAME);
}

async function readExistingLog(documentId: string): Promise<string> {
	if (logCache.has(documentId)) {
		return logCache.get(documentId)!;
	}

	try {
		const { exists, readTextFile: readFile } = await import("@tauri-apps/plugin-fs");
		const filePath = await getLogFilePath(documentId);
		if (await exists(filePath)) {
			const content = await readFile(filePath);
			logCache.set(documentId, content);
			return content;
		}
	} catch (err) {
		logger.debug("Could not read existing error log", { documentId, err });
	}
	return "";
}

async function persistLog(documentId: string, content: string): Promise<void> {
	const filePath = await getLogFilePath(documentId);
	logCache.set(documentId, content);

	const dotMomentPath = await getDocumentDotMomentPath(documentId);
	const { mkdir, exists: fsExists } = await import("@tauri-apps/plugin-fs");
	try {
		if (!(await fsExists(dotMomentPath))) {
			await mkdir(dotMomentPath, { recursive: true });
		}
	} catch {
		// Directory may already exist
	}

	await writeTextFile(filePath, content);
}

/**
 * Append a runtime error entry to the document's `.moment/runtime-errors.log`.
 * Safe to call from non-desktop environments (no-ops gracefully).
 * Writes are serialized per document to prevent data loss.
 */
async function appendRuntimeError(entry: RuntimeErrorEntry): Promise<void> {
	if (!env.isDesktopApp) {
		return;
	}

	const { documentId } = entry;
	const previous = writeQueue.get(documentId) ?? Promise.resolve();
	const next = previous
		.then(async () => {
			const existing = await readExistingLog(documentId);
			const newEntry = formatEntry(entry);
			const combined = existing ? `${existing}\n${newEntry}\n` : `${newEntry}\n`;
			const final = truncateIfNeeded(combined);
			await persistLog(documentId, final);
		})
		.catch((err) => {
			logger.error("Failed to write runtime error log", { documentId, err });
		});

	writeQueue.set(documentId, next);
	await next;
}

/**
 * Log a code cell runtime error.
 */
export function logCodeCellError(params: {
	documentId: string;
	pageId?: string;
	cellId?: string;
	error: Error | string;
}): void {
	const err = params.error instanceof Error ? params.error : new Error(String(params.error));
	void appendRuntimeError({
		timestamp: getTimestamp(),
		source: "code-cell",
		documentId: params.documentId,
		pageId: params.pageId,
		cellId: params.cellId,
		message: err.message,
		stack: err.stack,
	});
}

/**
 * Log a request page error.
 */
export function logRequestPageError(params: {
	documentId: string;
	pageId?: string;
	pageName?: string;
	error: Error | { message: string; code?: string } | string;
}): void {
	const message =
		params.error instanceof Error
			? params.error.message
			: typeof params.error === "string"
				? params.error
				: params.error.message;
	const stack = params.error instanceof Error ? params.error.stack : undefined;
	void appendRuntimeError({
		timestamp: getTimestamp(),
		source: "request-page",
		documentId: params.documentId,
		pageId: params.pageId,
		pageName: params.pageName,
		message,
		stack,
	});
}
